import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { addProduct, updateProduct } from "../../store/slices/productSlice";
import Button, { ButtonType } from "../../components/Assets/Button";
import { IProduct } from "../../models/productData.model";
import {
  IWarehouseRawMaterial,
  UNITS,
} from "../../models/warehouseRawMaterial.model";
import Select from "react-select";
import {
  getUnitsByType,
  getDefaultUnitForType,
} from "../../utils/warehouse.util";

interface ProductModalProps {
  isOpen: boolean;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  closeModal: () => void;
  product?: IProduct | null;
  isEditMode: boolean;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isModalOpen,
  closeModal,
  product,
  isEditMode = false,
  setIsModalOpen,
}) => {
  const [productName, setProductName] = useState("");
  const [hsn, setHsn] = useState<number | "">("");
  const [sku, setSku] = useState("");
  const [quantity, setQuantity] = useState<number | "">("");
  const [price, setPrice] = useState<number | "">("");
  const [msp, setMsp] = useState<number | "">("");
  const [gst, setGst] = useState<number | "">("");
  const [selectedRange, setSelectedRange] = useState("");
  const [rawMaterials, setRawMaterials] = useState<
    {
      rawMaterial: string;
      quantity: number | "";
      unit: string;
      unitType?: string;
    }[]
  >([]);

  const ranges = useSelector((state: RootState) => state.product.allRanges);
  const { rawMaterials: rawMaterialOptions } = useSelector(
    (state: RootState) => state.storeInward,
  );
  const dispatch = useDispatch<RootDispatch>();

  useEffect(() => {
    if (!isEditMode || !product) {
      setProductName("");
      setHsn("");
      setSku("");
      setQuantity("");
      setPrice("");
      setMsp("");
      setGst("");
      setSelectedRange("");
      setRawMaterials([]);
      return;
    }

    setProductName(product.productName);
    setHsn(product.hsn === "" ? "" : Number(product.hsn));
    setSku(product.sku || "");
    setQuantity(product.quantity || "");
    setPrice(product.price || "");
    setMsp(product.msp || "");
    setGst(product.gst || "");
    setSelectedRange(product?.range?._id || "");

    setRawMaterials(
      product.rawMaterials?.map((rm) => {
        const rawMaterialId =
          typeof rm.rawMaterial === "object"
            ? rm.rawMaterial._id
            : rm.rawMaterial;
        const rawMaterialData = rawMaterialOptions.find(
          (opt) => opt._id === rawMaterialId,
        );

        return {
          rawMaterial: rawMaterialId,
          quantity: rm.quantity || "",
          unit:
            rm.unit ||
            (rawMaterialData
              ? getDefaultUnitForType(rawMaterialData.unitType) || ""
              : ""),
          unitType: rawMaterialData?.unitType || "",
        };
      }) || [],
    );
  }, [isEditMode, product, rawMaterialOptions]);

  const validateForm = () => {
    if (!productName?.trim()) {
      toast.error("Product Name is required");
      return false;
    }
    if (!sku?.trim()) {
      toast.error("SKU is required");
      return false;
    }
    if (!msp || msp <= 0) {
      toast.error("MSP must be greater than 0");
      return false;
    }
    if (!hsn || hsn <= 0) {
      toast.error("HSN must be greater than 0");
      return false;
    }
    if (!price || price <= 0) {
      toast.error("Price must be greater than 0");
      return false;
    }
    if (!gst || gst < 0 || gst > 100) {
      toast.error("GST must be between 0 and 100");
      return false;
    }
    // if (!selectedRange) {
    //   toast.error("Range is required");
    //   return false;
    // }
    if (rawMaterials.length > 0) {
      for (let index = 0; index < rawMaterials.length; index++) {
        const rm = rawMaterials[index];
        if (!rm.rawMaterial) {
          toast.error(`Raw Material ${index + 1}: ID is required`);
          return false;
        }
        if (!rm.quantity || rm.quantity <= 0) {
          toast.error(
            `Raw Material ${index + 1}: Quantity must be greater than 0`,
          );
          return false;
        }
        if (!rm.unit) {
          toast.error(`Raw Material ${index + 1}: Unit is required`);
          return false;
        }
      }
    }
    return true;
  };

  const handleAddRawMaterial = () => {
    setRawMaterials([
      ...rawMaterials,
      { rawMaterial: "", quantity: "", unit: "", unitType: "" },
    ]);
  };

  const handleRemoveRawMaterial = (index: number) => {
    setRawMaterials(rawMaterials.filter((_, i) => i !== index));
  };

  const handleRawMaterialChange = (
    index: number,
    field: "rawMaterial" | "quantity" | "unit",
    value: string | number,
  ) => {
    const updatedRawMaterials = [...rawMaterials];
    if (field === "rawMaterial") {
      const selectedRawMaterial = rawMaterialOptions.find(
        (rm: IWarehouseRawMaterial) => rm._id === value,
      );
      updatedRawMaterials[index] = {
        ...updatedRawMaterials[index],
        rawMaterial: value as string,
        unitType: selectedRawMaterial?.unitType || "",
        unit: selectedRawMaterial
          ? getDefaultUnitForType(selectedRawMaterial.unitType) || ""
          : "",
      };
    } else {
      updatedRawMaterials[index] = {
        ...updatedRawMaterials[index],
        [field]: value,
      };
    }
    setRawMaterials(updatedRawMaterials);
  };

  const handleAddProduct = async () => {
    if (!validateForm()) {
      setIsModalOpen(true);
      return;
    }

    const newProduct = {
      productName,
      hsn: Number(hsn),
      price: Number(price),
      msp: Number(msp),
      gst: Number(gst),
      sku,
      range: selectedRange,
      rawMaterials: rawMaterials.map((rm) => ({
        rawMaterial: rm.rawMaterial,
        quantity: Number(rm.quantity),
        unit: rm.unit,
      })),
    };

    try {
      if (isEditMode && product) {
        await dispatch(
          updateProduct({
            id: product._id,
            payload: { updateData: newProduct },
          }),
        ).unwrap();
        toast.success("Product updated successfully");
      } else {
        await dispatch(addProduct(newProduct)).unwrap();
        toast.success("Product added successfully");
      }
      setIsModalOpen(false);
      setProductName("");
      setHsn("");
      setQuantity("");
      setPrice("");
      setMsp("");
      setGst("");
      setSelectedRange("");
      setSku("");
      setRawMaterials([]);
    } catch (error) {
      toast.error("Failed to add/update product");
    }
  };

  return (
    <div>
      {isModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex text-xs justify-center items-center p-4 z-[70]"
          onClick={closeModal}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
            className="bg-white p-8 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-lg z-[70]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-6">
              {isEditMode ? "Edit Product" : "Add New Product"}
            </h2>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <label htmlFor="productName" className="flex flex-col gap-2">
                  Product Name
                  <input
                    id="productName"
                    type="text"
                    placeholder="Product Name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </label>
                <label htmlFor="hsn" className="flex flex-col gap-2">
                  HSN
                  <input
                    id="hsn"
                    type="number"
                    placeholder="HSN"
                    value={hsn}
                    onChange={(e) => setHsn(Number(e.target.value) || "")}
                    min="0"
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </label>
                <label htmlFor="sku" className="flex flex-col gap-2">
                  SKU
                  <input
                    id="sku"
                    type="text"
                    placeholder="SKU"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </label>
                {isEditMode && (
                  <label htmlFor="quantity" className="flex flex-col gap-2">
                    Quantity
                    <input
                      id="quantity"
                      type="number"
                      placeholder="Quantity"
                      value={quantity}
                      disabled
                      className="p-2 border rounded bg-gray-100 cursor-not-allowed"
                    />
                  </label>
                )}
                <label htmlFor="price" className="flex flex-col gap-2">
                  Price
                  <input
                    id="price"
                    type="number"
                    placeholder="Price"
                    value={price}
                    onChange={(e) => setPrice(Number(e.target.value) || "")}
                    min="0"
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </label>
                <label htmlFor="msp" className="flex flex-col gap-2">
                  MSP (minimum selling price)
                  <input
                    id="msp"
                    type="number"
                    placeholder="MSP"
                    value={msp}
                    onChange={(e) => setMsp(Number(e.target.value) || "")}
                    min="0"
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </label>
                <label htmlFor="gst" className="flex flex-col gap-2">
                  GST (%)
                  <input
                    id="gst"
                    type="number"
                    placeholder="GST"
                    value={gst}
                    onChange={(e) => setGst(Number(e.target.value) || "")}
                    min="0"
                    max="100"
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </label>
                <label htmlFor="range" className="flex flex-col gap-2">
                  Range ( Optional )
                  <Select
                    id="range"
                    options={ranges.map((range: any) => ({
                      value: range.rangeId,
                      label: range.range,
                    }))}
                    value={
                      selectedRange
                        ? {
                            value: selectedRange,
                            label:
                              ranges.find(
                                (r: any) => r.rangeId === selectedRange,
                              )?.range || "",
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      setSelectedRange(
                        selectedOption ? selectedOption.value : "",
                      )
                    }
                    placeholder="Select Range"
                    isSearchable
                    isClearable
                  />
                </label>
              </div>

              <div className="flex flex-col gap-4">
                <h3 className="text-lg font-semibold">
                  Raw Materials (Usable Quantity)
                </h3>
                {rawMaterials.map((rm, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row gap-4 items-center"
                  >
                    <label className="flex flex-col gap-2 flex-1">
                      Raw Material
                      <Select
                        options={rawMaterialOptions.map(
                          (rm: IWarehouseRawMaterial) => ({
                            value: rm._id,
                            label: `${rm.name} (${rm.unitType}) (${rm.sku})`,
                          }),
                        )}
                        value={
                          rm.rawMaterial
                            ? rawMaterialOptions
                                .filter(
                                  (opt: IWarehouseRawMaterial) =>
                                    opt._id === rm.rawMaterial,
                                )
                                .map((opt: IWarehouseRawMaterial) => ({
                                  value: opt._id,
                                  label: `${opt.name} (${opt.sku})`,
                                }))[0]
                            : null
                        }
                        onChange={(selectedOption) =>
                          handleRawMaterialChange(
                            index,
                            "rawMaterial",
                            selectedOption ? selectedOption.value : "",
                          )
                        }
                        placeholder="Select Raw Material"
                        isSearchable
                        isClearable
                      />
                    </label>
                    <label className="flex flex-col gap-2 flex-1">
                      Quantity
                      <input
                        type="number"
                        placeholder="Quantity"
                        value={rm.quantity}
                        onChange={(e) =>
                          handleRawMaterialChange(
                            index,
                            "quantity",
                            Number(e.target.value) || "",
                          )
                        }
                        min="0"
                        className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="flex flex-col gap-2 flex-1">
                      Unit
                      <Select
                        options={
                          rm.unitType ? getUnitsByType(rm.unitType) : UNITS
                        }
                        value={
                          rm.unit
                            ? {
                                value: rm.unit,
                                label:
                                  UNITS.find((u) => u.value === rm.unit)
                                    ?.label || rm.unit,
                              }
                            : null
                        }
                        onChange={(selectedOption) =>
                          handleRawMaterialChange(
                            index,
                            "unit",
                            selectedOption ? selectedOption.value : "",
                          )
                        }
                        placeholder="Select Unit"
                        isSearchable
                        isClearable
                        className="text-sm"
                      />
                    </label>
                    <Button
                      type={ButtonType.Cancel}
                      onClick={() => handleRemoveRawMaterial(index)}
                      className="text-xs md:text-sm mt-6"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type={ButtonType.Submit}
                  onClick={handleAddRawMaterial}
                  className="text-xs md:text-sm"
                >
                  Add Raw Material
                </Button>
              </div>

              <div className="flex flex-col md:flex-row justify-end gap-2">
                <Button
                  type={ButtonType.Submit}
                  onClick={handleAddProduct}
                  className="text-xs md:text-sm"
                >
                  {isEditMode ? "Update Product" : "Add Product"}
                </Button>
                <Button
                  type={ButtonType.Cancel}
                  onClick={closeModal}
                  className="text-xs md:text-sm"
                >
                  Close
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ProductModal;
